/* kitsters-track-order.js
   Live order tracking on /pages/track-your-order.

   The customer types ONE thing - an order number or a tracking ID - and we
   resolve it and fetch the real status ourselves. There is deliberately no
   courier picker: iThink's API tells us the carrier, so asking the customer
   to know it was pushing our internal problem onto them.

   How the resolution works:
     - tracking ID  -> used directly
     - order number -> looked up in the signed-in customer's order/AWB map,
                       which Liquid renders into [data-ktrk-config]. iThink's
                       API accepts an AWB only (verified: posting an order_id
                       returns "AWB number is required"), so a signed-out
                       customer genuinely cannot be served this way and the UI
                       says so instead of failing vaguely.

   Every string from the API is inserted with textContent, never innerHTML -
   it is third-party data rendered on our domain.

   Every failure path ends somewhere useful (public tracker + email support). The
   endpoint is undocumented, so it WILL move eventually; when it does the page
   must degrade, not dead-end. */
(function () {
  'use strict';

  var root = document.querySelector('[data-ktrk-root]');
  if (!root) return;

  var reduce =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Config ───────────────────────────────────────────────── */
  var cfg = { api: {}, orders: [] };
  var cfgEl = document.querySelector('[data-ktrk-config]');
  if (cfgEl) {
    try {
      cfg = JSON.parse(cfgEl.textContent) || cfg;
    } catch (e) {
      cfg = { api: {}, orders: [] };
    }
  }
  if (!cfg.orders) cfg.orders = [];

  /* Order number -> AWB. Keyed without the '#' so "#13989" and "13989" both hit. */
  var orderMap = {};
  cfg.orders.forEach(function (o) {
    if (o && o.name && o.awb) orderMap[String(o.name).replace(/^#/, '').toUpperCase()] = o.awb;
  });

  /* ── Small DOM helpers (textContent only) ─────────────────── */
  function el(tag, className, text) {
    var n = document.createElement(tag);
    if (className) n.className = className;
    if (text != null) n.textContent = String(text);
    return n;
  }

  function fallbackUrl(awb) {
    return (cfg.fallbackUrl || '').replace('{awb}', encodeURIComponent(awb));
  }

  /* ── Status → tone. Drives the pill colour and nothing else, so an
     unrecognised status still renders correctly, just neutrally. ── */
  function toneFor(status) {
    var t = String(status || '').toLowerCase();
    if (t.indexOf('deliver') === 0 || t === 'delivered') return 'done';
    if (t.indexOf('out for delivery') > -1) return 'active';
    if (t.indexOf('transit') > -1 || t.indexOf('pickup') > -1 || t.indexOf('manifest') > -1) return 'active';
    if (t.indexOf('rto') > -1 || t.indexOf('undeliver') > -1 || t.indexOf('ndr') > -1 || t.indexOf('exception') > -1) return 'alert';
    return 'neutral';
  }

  /* ── Renderers ────────────────────────────────────────────── */
  var out = root.querySelector('[data-ktrk-result]');

  function clear() {
    while (out.firstChild) out.removeChild(out.firstChild);
  }

  function renderLoading() {
    clear();
    var box = el('div', 'ktrk__res ktrk__res--loading');
    box.appendChild(el('span', 'ktrk__spinner'));
    box.appendChild(el('p', 'ktrk__res-msg', 'Checking with our courier…'));
    out.appendChild(box);
  }

  function helpLinks(awb) {
    var row = el('div', 'ktrk__res-actions');
    if (awb) {
      var a = el('a', 'ktrk__btn ktrk__btn--ghost', 'Open the courier tracker');
      a.href = fallbackUrl(awb);
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      row.appendChild(a);
    }
    if (cfg.supportUrl) {
      var w = el('a', 'ktrk__btn ktrk__btn--ghost', 'Ask us by email');
      w.href = cfg.supportUrl;
      w.target = '_blank';
      w.rel = 'noopener noreferrer';
      row.appendChild(w);
    }
    return row;
  }

  function renderMessage(kind, title, body, awb, extraNode) {
    clear();
    var box = el('div', 'ktrk__res ktrk__res--' + kind);
    box.appendChild(el('h3', 'ktrk__res-title', title));
    if (body) box.appendChild(el('p', 'ktrk__res-msg', body));
    if (extraNode) box.appendChild(extraNode);
    box.appendChild(helpLinks(awb));
    out.appendChild(box);
  }

  function renderResult(details, history, awb) {
    clear();

    var od = details.order_details || {};
    var lg = details.logistics || {};
    var td = details.tracking_details || {};
    var cd = details.customer_details || {};
    var tone = toneFor(td.current_status_name);

    var box = el('div', 'ktrk__res ktrk__res--ok');

    /* Headline: status + the date line the courier gives us. */
    var head = el('div', 'ktrk__res-head');
    head.appendChild(el('span', 'ktrk__status ktrk__status--' + tone, td.current_status_name || 'Status unavailable'));

    if (td.current_status_date) {
      var when = el('p', 'ktrk__res-when');
      when.appendChild(el('span', 'ktrk__res-when-label', td.current_status_text || 'Expected'));
      when.appendChild(
        el(
          'strong',
          'ktrk__res-when-date',
          (td.current_status_day ? td.current_status_day + ', ' : '') + td.current_status_date
        )
      );
      head.appendChild(when);
    }
    box.appendChild(head);

    /* Meta row - everything we know, so the customer can sanity-check it is
       their parcel before reading the timeline. */
    var meta = el('ul', 'ktrk__res-meta');
    function metaItem(label, value) {
      if (!value) return;
      var li = el('li');
      li.appendChild(el('span', 'ktrk__res-meta-label', label));
      li.appendChild(el('span', 'ktrk__res-meta-value', value));
      meta.appendChild(li);
    }
    metaItem('Order', od.order_id);
    metaItem('Courier', lg.logistics_name);
    metaItem('Tracking ID', lg.awb_no || awb);
    metaItem('Delivering to', cd.city);
    if (meta.childNodes.length) box.appendChild(meta);

    /* Milestone stepper. */
    var miles = (history && history.forward_tracking_milestones) || [];
    if (miles.length) {
      var ol = el('ol', 'ktrk__mile');
      miles.forEach(function (m) {
        var done = m.status === 'completed';
        var li = el('li', 'ktrk__mile-step' + (done ? ' is-done' : ''));
        li.appendChild(el('span', 'ktrk__mile-dot'));
        li.appendChild(el('span', 'ktrk__mile-label', m.label));
        li.appendChild(el('span', 'ktrk__mile-time', m.datetime || ''));
        ol.appendChild(li);
      });
      box.appendChild(ol);
    }

    /* Full scan log, collapsed - useful when something has gone wrong, noise
       otherwise. */
    var acts = (history && history.activity_details) || [];
    if (acts.length) {
      var det = el('details', 'ktrk__log');
      var sum = el('summary', 'ktrk__log-summary', 'Full scan history');
      det.appendChild(sum);
      var list = el('ul', 'ktrk__log-list');
      acts.forEach(function (a) {
        (a.sub_activity || []).forEach(function (sa) {
          var li = el('li', 'ktrk__log-row');
          li.appendChild(el('span', 'ktrk__log-when', sa.status_date_time || ''));
          li.appendChild(el('span', 'ktrk__log-what', sa.status_title || a.activity_title || ''));
          list.appendChild(li);
        });
      });
      det.appendChild(list);
      box.appendChild(det);
    }

    box.appendChild(helpLinks(lg.awb_no || awb));
    out.appendChild(box);
  }

  /* ── Network ──────────────────────────────────────────────── */
  var inFlight = null;

  function post(url, awb, signal) {
    return fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ awb_no: awb }),
      signal: signal
    }).then(function (r) {
      if (!r.ok && r.status !== 422) throw new Error('http ' + r.status);
      return r.json();
    });
  }

  function lookup(awb) {
    if (inFlight) inFlight.abort();
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    inFlight = ctrl;
    var signal = ctrl ? ctrl.signal : undefined;

    renderLoading();

    /* The history call is a nice-to-have: a result with no timeline still
       answers "where is my parcel", so its failure must not fail the lookup. */
    return Promise.all([
      post(cfg.api.details, awb, signal),
      post(cfg.api.history, awb, signal).catch(function () { return null; })
    ])
      .then(function (res) {
        var details = res[0];
        if (!details || details.status !== 'success') {
          renderMessage(
            'empty',
            'We could not find that one yet',
            'Double-check the number for a typo. If you ordered very recently, tracking only appears once the parcel is dispatched and the courier makes its first scan - usually within a few hours.',
            awb
          );
          return;
        }
        renderResult(details, res[1], awb);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        renderMessage(
          'error',
          'Our tracker could not reach the courier',
          'This is on our side, not yours. Open the courier tracker directly with the button below, or message us and we will look it up for you.',
          awb
        );
      })
      .then(function () {
        inFlight = null;
      });
  }

  /* ── Input handling ───────────────────────────────────────── */
  var form = root.querySelector('[data-ktrk-form]');
  var input = root.querySelector('[data-ktrk-input]');

  function clean(v) {
    return (v || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  }

  /* Order numbers are short and all-digits (#13989); AWBs are longer and often
     carry letters (7D127280531, SF3677929963ITL). Anything 3-6 digits is an
     order number -- no real AWB in this store's history is that short. */
  function isOrderNumber(v) {
    return /^\d{3,6}$/.test(v);
  }

  function signInPrompt() {
    var wrap = document.createElement('div');
    var a = el('a', 'ktrk__btn ktrk__btn--indigo', 'Sign in to my account');
    a.href = cfg.accountUrl || '/account';
    wrap.appendChild(a);
    return wrap;
  }

  function submit(raw) {
    var v = clean(raw);

    if (!v) {
      renderMessage('empty', 'Pop a number in first', 'Enter the order number from your confirmation email, or the tracking ID from your shipping SMS.');
      if (input) input.focus();
      return;
    }

    if (isOrderNumber(v)) {
      var awb = orderMap[v];
      if (awb) {
        lookup(awb);
        return;
      }
      if (cfg.signedIn) {
        renderMessage(
          'empty',
          'No dispatched order with that number',
          'We could not match that order number to a dispatched parcel on your account. Check the number, or pick the order from the list beside this box - tracking only appears once an order has left our warehouse.'
        );
      } else {
        renderMessage(
          'empty',
          'Order numbers need you signed in',
          'Order numbers are private to your account, so we can only look one up once you are signed in. Sign in below - or paste the tracking ID from your shipping SMS, which works right away.',
          null,
          signInPrompt()
        );
      }
      return;
    }

    if (v.length < 6) {
      renderMessage('empty', 'That looks too short', 'Tracking IDs are at least 6 characters, like 7D127280531. Order numbers are 3-6 digits, like 13989.');
      if (input) input.focus();
      return;
    }

    lookup(v);
  }

  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      submit(input ? input.value : '');
    });
  }

  /* One-tap tracking from the signed-in order list. */
  root.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ktrk-quick]');
    if (!btn) return;
    e.preventDefault();
    var awb = btn.getAttribute('data-ktrk-quick');
    if (!awb) return;
    if (input) input.value = awb;
    lookup(clean(awb));
    if (out && out.scrollIntoView) out.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
  });

  /* Deep link: /pages/track-your-order?awb=XXXX tracks on load, so the number
     in a support reply or an SMS can be a single tap. */
  try {
    var qs = new URLSearchParams(window.location.search);
    var preset = qs.get('awb') || qs.get('tracking_number') || qs.get('order');
    if (preset) {
      if (input) input.value = preset;
      submit(preset);
    }
  } catch (e) {
    /* URLSearchParams missing - deep linking is a bonus, never a requirement. */
  }

  /* ── Scroll reveal ────────────────────────────────────────── */
  var reveals = root.querySelectorAll('.ktrk__reveal');
  if (reveals.length) {
    if (reduce || !('IntersectionObserver' in window)) {
      reveals.forEach(function (n) { n.classList.add('is-in'); });
    } else {
      var obs = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            obs.unobserve(entry.target);
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
      );
      reveals.forEach(function (n) { obs.observe(n); });
    }
  }
})();
